import { Box, LinearProgress, Typography } from "@mui/joy";
import TeacherImage from "../../images/Teacher.png";
import { AccessTime, DoneAll, Whatshot } from "@mui/icons-material";
import Metric from "../../components/home_screen/metric";
import ChapterCard from "../../components/home_screen/chapter-card";
import { palette } from "../../theme";
import { Feature } from "../../store/api/umbraco-api";
import { useCourses } from "../../context/courses-context";
import {
  useGetCourseConditionsQuery,
  useGetUserSignatureStatusQuery,
  useSignCourseConditionsMutation,
  useSignPrivacyConditionsMutation,
  useSignUserConditionsMutation,
  // Legacy imports for backward compatibility
  useGetCoursePolicyAcceptedQuery,
  useGetCoursePrivacyPolicyQuery,
  useAcceptCoursePolicyMutation,
} from "../../store/api/privacy-policy-api";
import CourseConditionsModal from "../../components/policy/course-conditions-modal";
import { useEffect, useState } from "react";

type NumericFeatureType = number;

// Update the Feature interface to use the numeric type
export interface FeatureWithNumericType extends Omit<Feature, "type"> {
  type?: NumericFeatureType;
}

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ mb: 4 }}>
    <Typography level="h3" sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const CourseScreen = () => {
  const {
    selectedCourseData: course,
    chapters,
    isLoading,
    isChaptersLoading,
    completedChaptersCount,
    inProgressChaptersCount,
  } = useCourses();

  const [conditionsOpen, setConditionsOpen] = useState(false);

  /* ------------------------------------------------------------------ */
  /* Course conditions state & queries                                   */
  /* ------------------------------------------------------------------ */
  const courseId = course?.id;
  
  // Enhanced conditions API
  const {
    data: conditionsResponse,
    isLoading: isConditionsLoading,
  } = useGetCourseConditionsQuery(
    { courseId: courseId || 0 },
    { skip: !courseId }
  );

  const {
    data: signatureStatus,
    isLoading: isSignatureStatusLoading,
    refetch: refetchSignatureStatus,
  } = useGetUserSignatureStatusQuery(
    { courseId: courseId || 0 },
    { skip: !courseId || !conditionsResponse?.exists }
  );

  const [signConditions, { isLoading: isSigning }] =
    useSignCourseConditionsMutation();

  const [signPrivacyConditions, { isLoading: isSigningPrivacy }] =
    useSignPrivacyConditionsMutation();

  const [signUserConditions, { isLoading: isSigningUser }] =
    useSignUserConditionsMutation();

  // Legacy privacy policy fallback for backward compatibility
  const {
    data: legacyPolicyData,
    isLoading: isLegacyPolicyLoading,
  } = useGetCoursePrivacyPolicyQuery(
    { courseId: courseId || 0 },
    { skip: !courseId || !!conditionsResponse?.exists }
  );

  const {
    data: legacyAcceptedData,
    isLoading: isLegacyAcceptedLoading,
    refetch: refetchLegacyAccepted,
  } = useGetCoursePolicyAcceptedQuery(
    { courseId: courseId || 0 },
    { skip: !courseId || !!conditionsResponse?.exists }
  );

  const [acceptLegacyPolicy, { isLoading: isAcceptingLegacy }] =
    useAcceptCoursePolicyMutation();

  // Session-persistent flag to prevent modal re-appearing after signing until backend changes
  const getSessionKey = () => `course-conditions-signed-${courseId}`;
  
  const getLocalSignedThisSession = (): boolean => {
    if (!courseId) return false;
    try {
      const stored = localStorage.getItem(getSessionKey());
      if (!stored) return false;
      
      const data = JSON.parse(stored);
      // Check if stored data is recent (within last 24 hours) to handle edge cases
      const isRecent = Date.now() - data.timestamp < 24 * 60 * 60 * 1000;
      return isRecent && !!data.signed;
    } catch {
      return false;
    }
  };
  
  const setLocalSignedThisSession = (signed: boolean) => {
    if (!courseId) return;
    try {
      if (signed) {
        localStorage.setItem(getSessionKey(), JSON.stringify({
          signed: true,
          timestamp: Date.now()
        }));
      } else {
        localStorage.removeItem(getSessionKey());
      }
    } catch {
      // localStorage might not be available, continue without persistence
    }
  };

  const [localSignedThisSession, setLocalSignedState] = useState(getLocalSignedThisSession());
  
  // Update local state when courseId changes (keeping localStorage as backup for edge cases)
  useEffect(() => {
    setLocalSignedState(getLocalSignedThisSession());
  }, [courseId]);

  // Enhanced helper functions that use the real backend individual tracking
  const needsPrivacySignature = (): boolean => {
    if (!signatureStatus || !conditionsResponse?.conditions) {
      console.log("[needsPrivacySignature] Early return - missing data:", { signatureStatus, conditionsResponse });
      return false;
    }

    // PRIMARY CHECK: Version comparison - if user signed current version, they don't need to sign again
    if (signatureStatus.signedVersion && signatureStatus.currentVersion) {
      const isUpToDate = signatureStatus.signedVersion >= signatureStatus.currentVersion;
      console.log("[needsPrivacySignature] Version check - PRIMARY:", {
        signedVersion: signatureStatus.signedVersion,
        currentVersion: signatureStatus.currentVersion,
        isUpToDate,
        message: isUpToDate ? "User has signed current version - NO SIGNING NEEDED" : "User needs to sign updated version"
      });
      
      if (isUpToDate) {
        // User has signed the current or newer version - no signing needed
        return false;
      }
    }

    // Use the new enhanced backend response format
    if (signatureStatus.individual?.privacy) {
      const privacyInfo = signatureStatus.individual.privacy;
      const needsSigning = privacyInfo.visible && !privacyInfo.signed;
      console.log("[needsPrivacySignature] Using enhanced backend tracking:", {
        visible: privacyInfo.visible,
        signed: privacyInfo.signed,
        needsSigning
      });
      return needsSigning;
    }

    // Fallback to legacy fields if available
    if (signatureStatus.hasSignedPrivacy !== undefined && signatureStatus.needsToSignPrivacy !== undefined) {
      console.log("[needsPrivacySignature] Using legacy backend individual tracking:", signatureStatus.needsToSignPrivacy);
      return !!signatureStatus.needsToSignPrivacy;
    }
    
    // Final fallback logic for older backend versions
    const neverSigned = !signatureStatus.signedVersion || signatureStatus.signedVersion === 0;
    const conditionsUpdatedWithMustResign = 
      !!signatureStatus.signedVersion && 
      signatureStatus.signedVersion < signatureStatus.currentVersion && 
      conditionsResponse.conditions.makeUsersSignAgain;
    
    const needsSignature = neverSigned || conditionsUpdatedWithMustResign;
    
    // Check if privacy conditions exist and are shown
    const hasPrivacyConditions = !!(conditionsResponse.conditions.privacyConditionText && 
                                    conditionsResponse.conditions.privacyConditionText.trim().length > 0);
    const showPrivacyConditions = signatureStatus.showPrivacyConditions !== undefined 
      ? !!signatureStatus.showPrivacyConditions 
      : hasPrivacyConditions; // Fallback: show if conditions exist
    
    const result = needsSignature && showPrivacyConditions;
    
    console.log("[needsPrivacySignature] Fallback calculation:", {
      neverSigned,
      conditionsUpdatedWithMustResign,
      needsSignature,
      hasPrivacyConditions,
      showPrivacyConditions,
      result,
      privacyText: conditionsResponse.conditions.privacyConditionText?.substring(0, 50),
    });
    
    return result;
  };

  const needsUserSignature = (): boolean => {
    if (!signatureStatus || !conditionsResponse?.conditions) {
      console.log("[needsUserSignature] Early return - missing data:", { signatureStatus, conditionsResponse });
      return false;
    }
    
    // PRIMARY CHECK: Version comparison - if user signed current version, they don't need to sign again
    if (signatureStatus.signedVersion && signatureStatus.currentVersion) {
      const isUpToDate = signatureStatus.signedVersion >= signatureStatus.currentVersion;
      console.log("[needsUserSignature] Version check - PRIMARY:", {
        signedVersion: signatureStatus.signedVersion,
        currentVersion: signatureStatus.currentVersion,
        isUpToDate,
        message: isUpToDate ? "User has signed current version - NO SIGNING NEEDED" : "User needs to sign updated version"
      });
      
      if (isUpToDate) {
        // User has signed the current or newer version - no signing needed
        return false;
      }
    }
    
    // Use the new enhanced backend response format
    if (signatureStatus.individual?.user) {
      const userInfo = signatureStatus.individual.user;
      const needsSigning = userInfo.visible && !userInfo.signed;
      console.log("[needsUserSignature] Using enhanced backend tracking:", {
        visible: userInfo.visible,
        signed: userInfo.signed,
        needsSigning
      });
      return needsSigning;
    }

    // Fallback to legacy fields if available
    if (signatureStatus.hasSignedUser !== undefined && signatureStatus.needsToSignUser !== undefined) {
      console.log("[needsUserSignature] Using legacy backend individual tracking:", signatureStatus.needsToSignUser);
      return !!signatureStatus.needsToSignUser;
    }
    
    // Final fallback logic for older backend versions
    const neverSigned = !signatureStatus.signedVersion || signatureStatus.signedVersion === 0;
    const conditionsUpdatedWithMustResign = 
      !!signatureStatus.signedVersion && 
      signatureStatus.signedVersion < signatureStatus.currentVersion && 
      conditionsResponse.conditions.makeUsersSignAgain;
    
    const needsSignature = neverSigned || conditionsUpdatedWithMustResign;
    
    // Check if user conditions exist and are shown
    const hasUserConditions = !!(conditionsResponse.conditions.userConditionText && 
                                 conditionsResponse.conditions.userConditionText.trim().length > 0);
    const showUserConditions = signatureStatus.showUserConditions !== undefined 
      ? !!signatureStatus.showUserConditions 
      : hasUserConditions; // Fallback: show if conditions exist
    
    const result = needsSignature && showUserConditions;
    
    console.log("[needsUserSignature] Fallback calculation:", {
      neverSigned,
      conditionsUpdatedWithMustResign,
      needsSignature,
      hasUserConditions,
      showUserConditions,
      result,
      userText: conditionsResponse.conditions.userConditionText?.substring(0, 50),
    });
    
    return result;
  };

  // Determine if modal should be shown based on business logic
  let shouldShowModal = false;
  
  if (conditionsResponse?.exists && conditionsResponse.conditions) {
    // Enhanced conditions system - check if any condition type needs signing
    const needsPrivacy = !!needsPrivacySignature(); // Ensure boolean
    const needsUser = !!needsUserSignature(); // Ensure boolean
    const needsSignature = needsPrivacy || needsUser;
    
    shouldShowModal = !!(courseId &&
      !isConditionsLoading &&
      !isSignatureStatusLoading &&
      conditionsResponse.conditions.isActive &&
      conditionsResponse.conditions.visibility &&
      needsSignature);
    
    console.log("[shouldShowModal] Enhanced conditions logic:", {
      courseId: !!courseId,
      isConditionsLoading,
      isSignatureStatusLoading,
      isActive: conditionsResponse.conditions.isActive,
      visibility: conditionsResponse.conditions.visibility,
      needsPrivacy,
      needsUser,
      needsSignature,
      shouldShowModal,
      backendSignatureData: {
        individual: signatureStatus?.individual,
        signedVersion: signatureStatus?.signedVersion,
        currentVersion: signatureStatus?.currentVersion,
        hasSignedPrivacy: signatureStatus?.hasSignedPrivacy,
        hasSignedUser: signatureStatus?.hasSignedUser,
        versionComparison: signatureStatus?.signedVersion && signatureStatus?.currentVersion ? {
          userSignedVersion: signatureStatus.signedVersion,
          currentConditionsVersion: signatureStatus.currentVersion,
          isUpToDate: signatureStatus.signedVersion >= signatureStatus.currentVersion,
          verdict: signatureStatus.signedVersion >= signatureStatus.currentVersion ? "NO SIGNING NEEDED" : "SIGNING REQUIRED"
        } : "no version data available"
      }
    });
  } else if (!conditionsResponse || (!conditionsResponse.exists && !isConditionsLoading)) {
    // Legacy privacy policy fallback
    const policyExists = !!(legacyPolicyData as any)?.policy || !!(legacyPolicyData as any)?.content;
    const hasAccepted =
      localSignedThisSession ||
      legacyAcceptedData?.accepted ||
      (legacyAcceptedData as any)?.accepted ||
      (legacyAcceptedData as any)?.Accepted ||
      false;

    shouldShowModal = !!(courseId &&
      !isLegacyPolicyLoading &&
      !isLegacyAcceptedLoading &&
      policyExists &&
      !hasAccepted);
    
    console.log("[shouldShowModal] Legacy fallback logic:", {
      courseId: !!courseId,
      isLegacyPolicyLoading,
      isLegacyAcceptedLoading,
      policyExists,
      hasAccepted,
      shouldShowModal
    });
  } else {
    console.log("[shouldShowModal] No conditions to check:", {
      conditionsResponse,
      isConditionsLoading
    });
  }

  useEffect(() => {
    setConditionsOpen(shouldShowModal);
  }, [shouldShowModal]);

  // Debugging output
  useEffect(() => {
    console.log("[CourseConditions] state", {
      courseId,
      conditionsResponse,
      signatureStatus,
      isConditionsLoading,
      isSignatureStatusLoading,
      localSignedThisSession,
      shouldShowModal,
      businessLogic: conditionsResponse?.exists && signatureStatus && {
        visibility: conditionsResponse.conditions?.visibility,
        isActive: conditionsResponse.conditions?.isActive,
        makeUsersSignAgain: conditionsResponse.conditions?.makeUsersSignAgain,
        needsPrivacySignature: needsPrivacySignature(),
        needsUserSignature: needsUserSignature(),
        enhancedTracking: signatureStatus.individual && {
          privacy: signatureStatus.individual.privacy,
          user: signatureStatus.individual.user,
        },
        legacyTracking: {
          hasSignedPrivacy: signatureStatus.hasSignedPrivacy,
          hasSignedUser: signatureStatus.hasSignedUser,
          needsToSignPrivacy: signatureStatus.needsToSignPrivacy,
          needsToSignUser: signatureStatus.needsToSignUser,
        },
        versions: {
          signed: signatureStatus.signedVersion,
          current: signatureStatus.currentVersion
        }
      },
      legacyFallback: !conditionsResponse?.exists && {
        legacyPolicyData,
        legacyAcceptedData,
        isLegacyPolicyLoading,
        isLegacyAcceptedLoading,
      }
    });
  }, [courseId, conditionsResponse, signatureStatus, isConditionsLoading, isSignatureStatusLoading, 
      localSignedThisSession, shouldShowModal, legacyPolicyData, legacyAcceptedData, 
      isLegacyPolicyLoading, isLegacyAcceptedLoading]);

  if (isLoading || isChaptersLoading) {
    return <LinearProgress sx={{ width: "600px" }} />;
  }

  return (
    <>
      {/* Course Conditions Modal – blocks the UI until conditions are signed */}
      <CourseConditionsModal
        open={conditionsOpen}
        conditions={conditionsResponse?.conditions}
        signatureStatus={signatureStatus}
        loading={
          conditionsResponse?.exists 
            ? (isConditionsLoading || isSignatureStatusLoading || isSigning || isSigningPrivacy || isSigningUser)
            : (isLegacyPolicyLoading || isLegacyAcceptedLoading || isAcceptingLegacy)
        }
        onSignConditions={async () => {
          if (!courseId) return;

          try {
            let response: any = null;

            if (conditionsResponse?.exists && conditionsResponse.conditions) {
              // Determine what needs to be signed
              const needsPrivacy = needsPrivacySignature();
              const needsUser = needsUserSignature();

              console.log("[CourseConditions] Signing scenario:", { needsPrivacy, needsUser });

              if (needsPrivacy && needsUser) {
                // Scenario 3: Both conditions need signing - use combined endpoint
                console.log("[CourseConditions] Signing both conditions");
                response = await signConditions({ courseId }).unwrap();
                console.log("[CourseConditions] Both conditions signed:", response);
              } else if (needsPrivacy) {
                // Scenario 1: Only privacy needs signing
                console.log("[CourseConditions] Signing privacy only");
                response = await signPrivacyConditions({ courseId }).unwrap();
                console.log("[CourseConditions] Privacy signed:", response);
              } else if (needsUser) {
                // Scenario 2: Only user conditions need signing
                console.log("[CourseConditions] Signing user conditions only");
                response = await signUserConditions({ courseId }).unwrap();
                console.log("[CourseConditions] User conditions signed:", response);
              } else {
                console.log("[CourseConditions] No conditions need signing - this shouldn't happen");
                return;
              }

              // Show user-friendly message from backend
              if (response?.message) {
                console.log("[CourseConditions] Backend message:", response.message);
                // TODO: Show toast notification with response.message
              }

              // Refresh server state to get updated individual status
              console.log("[CourseConditions] Refreshing signature status from backend...");
              const updatedStatus = await refetchSignatureStatus();
              console.log("[CourseConditions] Updated signature status:", updatedStatus.data);
              
              // Close modal - backend version check will prevent it from showing again
              setConditionsOpen(false);
              
              console.log("[CourseConditions] Signing complete, modal closed");
            } else {
              // Legacy privacy policy fallback
              response = await acceptLegacyPolicy({ courseId }).unwrap();
              console.log("[LegacyPrivacyPolicy] accept response", response);
              
              refetchLegacyAccepted();
              setConditionsOpen(false);
            }

            return response;
          } catch (e) {
            console.error("Failed to sign conditions", e);
            throw e; // Re-throw to let modal handle the error
          }
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          paddingTop: { xs: 2, md: 4 },
          height: "100%", // Take full height of parent
          overflow: "auto", // Enable scrolling
          padding: { xs: 2, md: 4 }, // Add padding on all sides
        }}
      >
        <Box
          sx={{
            display: { xs: "grid", md: "flex" }, // Use grid on smaller screens, flex on larger screens
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, // Single column for very small screens, two columns for small screens
            alignItems: "center",
            justifyContent: "space-between", // Spacing in flex mode
            gap: 2, // Spacing between grid items
          }}
        >
          <Box sx={{ display: "grid", gap: 2, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between", // Spacing in flex mode
              }}
            >
              <Typography
                sx={{
                  alignSelf: "center",
                }}
                level="h1"
              >
                {course?.name}
              </Typography>
              <Box
                component="img"
                src={TeacherImage}
                alt="logo"
                sx={{
                  maxHeight: { xs: 200, sm: 300, xl: 350 }, // Scale the max height for different screen sizes
                  width: { xs: "40%", sm: "40%" }, // Full width for small screens, auto for larger screens
                  display: { md: "none" }, // Hide on small screens, show on medium and larger screens
                }}
              />
            </Box>

            <Box
              sx={{
                display: "grid", // Grid layout for metrics
                gap: 1,
                gridTemplateColumns: {
                  sm: "repeat(1, 1fr)",
                  md: "repeat(3, 1fr)",
                }, // Adjust columns based on screen size
              }}
            >
              <Metric
                mainColor={palette.purple}
                bgcolor={palette.purple_light}
                label="פרקים שהושלמו"
                value={completedChaptersCount}
                icon={
                  <DoneAll
                    sx={{
                      fontSize: "36px",
                      color: palette.purple,
                    }}
                  />
                }
              />
              <Metric
                mainColor={palette.yellow}
                label="פרקים בתהליך"
                value={inProgressChaptersCount}
                bgcolor={palette.yellow_light}
                icon={
                  <AccessTime
                    sx={{
                      fontSize: "36px",
                      color: palette.yellow,
                    }}
                  />
                }
              />
              <Metric
                mainColor={palette.orange}
                bgcolor={palette.orange_light}
                label="סטריק יומי"
                value={course?.dailyStreak || 0}
                icon={
                  <Whatshot
                    sx={{
                      fontSize: "36px",
                      color: palette.orange,
                    }}
                  />
                }
              />
            </Box>
          </Box>

          <Box
            component="img"
            src={TeacherImage}
            alt="logo"
            sx={{
              maxHeight: { xs: 200, sm: 300, xl: 350 }, // Scale the max height for different screen sizes
              width: { sm: "40%", md: "30%", lg: "40%" }, // Full width for small screens, auto for larger screens
              objectFit: "contain", // Maintain aspect ratio and prevent cropping
              alignSelf: { xs: "center", md: "flex-end" }, // Center on small screens, align to the right on larger screens
              display: { xs: "none", md: "block" }, // Hide on small screens, show on medium and larger screens
            }}
          />
        </Box>

        <Section title="פרקי למידה">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            {chapters &&
              chapters.map((chapter, index) => {
                return (
                  <ChapterCard
                    courseId={course?.id || 0}
                    id={chapter.id || 0}
                    key={index}
                    title={chapter.title}
                    total={chapter.total}
                    completed={chapter.completed}
                    learningMinutes={chapter.learningMinutes}
                    features={
                      chapter.features as unknown as FeatureWithNumericType[]
                    } // Add type assertion here
                  />
                );
              })}
          </Box>
        </Section>
      </Box>
    </>
  );
};

export default CourseScreen;
